import type { TranslationKey } from "@privacy/translations";
import type { ComponentProps, ReactNode } from "react";
import type { PartnerRole, RoleActiveStatus } from "../../api/partners";
import {
  DatabaseIcon,
  AppWindowIcon,
  BlendIcon,
  ArrowBigDownDashIcon,
  ComponentIcon,
  IconWithBackground,
  type IconWithBackgroundProps,
} from "../../shared/icons";
import { ConeIcon } from "lucide-react";

export const ROLES_INFO = {
  processor: {
    labelKey: "Partner.Role.Processor",
    descriptionKey: "Partner.Role.Processor.Description",
    renderIcon: props => (
      <RoleIcon
        Icon={DatabaseIcon}
        backgroundColor={theme => theme.palette.brandBlue["100"]}
        borderColor={theme => theme.palette.brandBlue["100"]}
        color={theme => theme.palette.brandBlue["500"]}
        {...props}
      />
    ),
  },
  controllerOurClient: {
    labelKey: "Partner.Role.ControllerOurClient",
    descriptionKey: "Partner.Role.ControllerOurClient.Description",
    renderIcon: props => (
      <RoleIcon
        Icon={ComponentIcon}
        backgroundColor={theme => theme.palette.teal["100"]}
        borderColor={theme => theme.palette.teal["100"]}
        color={theme => theme.palette.teal["500"]}
        {...props}
      />
    ),
  },

  organisationWeUseAsSubProcessor: {
    labelKey: "Partner.Role.OrganisationWeUseAsSubProcessor",
    descriptionKey: "Partner.Role.OrganisationWeUseAsSubProcessor.Description",
    renderIcon: props => (
      <RoleIcon
        Icon={ConeIcon}
        backgroundColor={theme => theme.palette.grey["300"]}
        borderColor={theme => theme.palette.grey["300"]}
        color={theme => theme.palette.text.tertiary}
        {...props}
      />
    ),
  },
  systemProvider: {
    labelKey: "Partner.Role.SystemProvider",
    descriptionKey: "Partner.Role.SystemProvider.Description",
    renderIcon: props => (
      <RoleIcon
        Icon={AppWindowIcon}
        backgroundColor={theme => theme.palette.purple["50"]}
        borderColor={theme => theme.palette.purple["50"]}
        color={theme => theme.palette.purple["400"]}
        {...props}
      />
    ),
  },
  jointController: {
    labelKey: "Partner.Role.JointController",
    descriptionKey: "Partner.Role.JointController.Description",
    renderIcon: props => (
      <RoleIcon
        Icon={BlendIcon}
        backgroundColor={theme => theme.palette.brandGreen["100"]}
        borderColor={theme => theme.palette.brandGreen["100"]}
        color={theme => theme.palette.brandGreen["500"]}
        {...props}
      />
    ),
  },
  recipient: {
    labelKey: "Partner.Role.Recipient",
    descriptionKey: "Partner.Role.Recipient.Description",
    renderIcon: props => (
      <RoleIcon
        Icon={ArrowBigDownDashIcon}
        backgroundColor={theme => theme.palette.brandRed["100"]}
        borderColor={theme => theme.palette.brandRed["100"]}
        color={theme => theme.palette.brandRed["500"]}
        {...props}
      />
    ),
  },
} satisfies Record<
  PartnerRole,
  {
    renderIcon: ({
      size,
      activeStatus,
    }: {
      size: IconWithBackgroundProps["size"];
      activeStatus: RoleActiveStatus;
    }) => ReactNode;
    labelKey: TranslationKey;
    descriptionKey: TranslationKey;
  }
>;

const RoleIcon = ({
  Icon,
  size,
  backgroundColor,
  borderColor,
  color,
  activeStatus,
}: ComponentProps<typeof IconWithBackground> & { activeStatus: RoleActiveStatus }) => {
  return (
    <IconWithBackground
      Icon={Icon}
      backgroundColor={theme => (activeStatus === "active" ? backgroundColor(theme) : "transparent")}
      borderColor={theme => (activeStatus === "active" ? borderColor(theme) : theme.palette.grey["500"])}
      color={theme => (activeStatus === "active" ? color(theme) : theme.palette.text.tertiary)}
      size={size}
    />
  );
};
